import { Box, Radio, RadioGroup, FormControlLabel, Select, MenuItem, Popper, Paper, Button, IconButton, FormControl } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Iconify from '../components/Iconify';
import { FormatDate } from './formatDate';
import { useState, useRef } from 'react';
import { fr } from 'date-fns/locale';

const CustomDatePicker = ({ value, onChange, fromTable = false }) => {

    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [selectionType, setSelectionType] = useState('before');
    const [singleDate, setSingleDate] = useState(value?.date || null);
    const [startDate, setStartDate] = useState(value?.startDate || null);
    const [endDate, setEndDate] = useState(value?.endDate || null);

    const handleApply = () => {
        if (selectionType === 'before' && singleDate) {
            onChange({ type: 'before', date: singleDate });
        } else if (selectionType === 'after' && singleDate) {
            onChange({ type: 'after', date: singleDate });
        } else if (selectionType === 'between' && startDate && endDate) {
            onChange({ type: 'between', startDate, endDate });
        }
        setOpen(false);
    };

    const handleClear = () => {
        setSelectionType('');
        setSingleDate(null);
        setStartDate(null);
        setEndDate(null);
        onChange(null);
        setOpen(false);
    };

    const getDisplayText = () => {
        if (!value) return 'Date';
        if (value.type === 'before') return `Avant ${FormatDate(value.date.toLocaleDateString())}`;
        if (value.type === 'after') return `Après ${FormatDate(value.date.toLocaleDateString())}`;
        if (value.type === 'between') {
            return `${FormatDate(value.startDate.toLocaleDateString())} - ${FormatDate(value.endDate.toLocaleDateString())}`;
        }
        return 'Date';
    };

    const selectStyles = {
        '&:before': {
            borderBottom: '1px solid #EA5E00',
        },
        '&:after': {
            borderBottom: '2px solid #EA5E00',
        },
        '&:hover:not(.Mui-disabled):before': {
            borderBottom: '2px solid #EA5E00',
        },
        minWidth: 'auto',
        '& .MuiSelect-icon': {
            color: '#EA5E00',
            top: '50%',
            transform: 'translateY(-50%)',
        },
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            {fromTable ? (
                <FormControl fullWidth variant="standard">
                    <Select
                        ref={anchorRef}
                        displayEmpty
                        sx={selectStyles}
                        onClick={() => setOpen(!open)}
                        value={new Date(value) || ''}
                        open={false}
                        renderValue={() => (
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <span>{getDisplayText()}</span>
                                {value?.type && (
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleClear();
                                        }}
                                        sx={{ p: 0, ml: 1 }}
                                    >
                                        <Iconify icon="ic:baseline-clear" sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    >
                        <MenuItem style={{ display: 'none' }} />
                    </Select>
                </FormControl>
            ) : (
                <Select
                    ref={anchorRef}
                    className="invoiceFiltration_select"
                    size="small"
                    onClick={() => setOpen(!open)}
                    value={new Date(value) || ''}
                    open={false}
                    displayEmpty
                    startAdornment={
                        <Iconify
                            icon="teenyicons:calendar-outline"
                            className="invoiceFiltration_select_icon"
                        />
                    }
                    renderValue={() => (
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                            <span>{getDisplayText()}</span>
                            {value?.type && (
                                <IconButton
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleClear();
                                    }}
                                    sx={{ p: 0, ml: 1 }}
                                >
                                    <Iconify icon="ic:baseline-clear" sx={{ fontSize: 16 }} />
                                </IconButton>
                            )}
                        </Box>
                    )}
                >
                    <MenuItem style={{ display: 'none' }} />
                </Select>
            )}

            <ClickAwayListener
                onClickAway={(event) => {
                    // Ensure the click is outside the Select and Popper
                    if (!anchorRef.current?.contains(event.target)) {
                        setOpen(false);
                    }
                }}
            >
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                >
                    <Paper sx={{ p: 2, width: 400 }}>
                        <RadioGroup
                            value={selectionType}
                            onChange={(e) => setSelectionType(e.target.value)}
                            row
                            sx={{ mb: 2 }}
                        >
                            <FormControlLabel value="before" control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#EA5E00' } }} />} label="Avant" />
                            <FormControlLabel value="after" control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#EA5E00' } }} />} label="Après" />
                            <FormControlLabel value="between" control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#EA5E00' } }} />} label="Entre" />
                        </RadioGroup>

                        {selectionType !== 'between' ? (
                            <DatePicker
                                label="Sélectionnez une date"
                                value={singleDate}
                                onChange={setSingleDate}
                                format="dd/MM/yyyy"
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <DatePicker
                                    label="Date de début"
                                    value={startDate}
                                    onChange={setStartDate}
                                    format="dd/MM/yyyy"
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                        },
                                    }}
                                />
                                <DatePicker
                                    label="Date de fin"
                                    value={endDate}
                                    onChange={setEndDate}
                                    format="dd/MM/yyyy"
                                    minDate={startDate}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button className="secondary_button" onClick={handleClear}>
                                Effacer
                            </Button>
                            <Button className="primary_button" onClick={handleApply}>
                                Appliquer
                            </Button>
                        </Box>
                    </Paper>
                </Popper>
            </ClickAwayListener>
        </LocalizationProvider>
    );
};

export default CustomDatePicker;